import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PlatformWizard from './components/PlatformWizard';
import { SimpleLeagueInput } from './components/SimpleLeagueInput';
import { YahooLogin } from './components/YahooLogin';
import { TeamSelector } from './components/TeamSelector';
import { MatchupSelector } from './components/MatchupSelector';
import { HostSelector } from './components/HostSelector';
import { LeadCaptureForm } from './components/LeadCaptureForm';
import { CallView } from './components/CallView';
import { SharedRecordingView } from './components/SharedRecordingView';
import { ESPNPrivateLeague } from './components/ESPNPrivateLeague';
import { ErrorMessage } from './components/ErrorMessage';
import { LoadingSpinner } from './components/LoadingSpinner';
import { PoweredByYobi } from './components/PoweredByYobi';
import TestCallPage from './components/TestCallPage';
import { fantasyApi, LeagueInput as LeagueInputType } from './services/fantasyApi';
import { League, Roster, Matchup, User } from './types/fantasy';
import { generatePodcastPrompt, PodcastPromptData } from './services/podcastPromptGenerator';
import { playerDataService } from './services/playerDataService';
import { analytics, trackWizardStep, trackError } from './services/analytics';
import { theme } from './styles/theme';

const AppContainer = styled.div`
  min-height: 100vh;
  background: ${theme.background.main};
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${theme.background.overlay};
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const MainContent = styled.main`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 40px);
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    min-height: calc(100vh - 40px);
  }
`;

const Navigation = styled.nav`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const NavButton = styled.button<{ active?: boolean }>`
  padding: 10px 20px;
  background: ${props => props.active ? theme.colors.button.primary.background : theme.colors.glass.background};
  color: ${theme.colors.text.primary};
  border: 1px solid ${props => props.active ? theme.colors.primary : theme.colors.glass.border};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  backdrop-filter: blur(16px);
  font-family: ${theme.typography.fontFamily};
  
  &:hover {
    background: ${props => props.active ? theme.colors.button.primary.background : theme.colors.glass.backgroundStrong};
    border-color: ${props => props.active ? theme.colors.secondary : theme.colors.glass.borderStrong};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glass};
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

const ResetButton = styled.button`
  background: ${theme.colors.accent};
  color: ${theme.colors.text.primary};
  border: 1px solid ${theme.colors.glass.border};
  padding: 10px 20px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  backdrop-filter: blur(16px);
  font-family: ${theme.typography.fontFamily};
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glass};
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

const HelperTextContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  ${theme.glassContainer.normal}
  padding: 16px 32px;
  text-align: center;
  z-index: 10;
  
  @media (max-width: 768px) {
    width: calc(100% - 40px);
    padding: 12px 20px;
    top: 10px;
  }
`;

const HelperText = styled.p`
  margin: 0;
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text.primary};
  line-height: 1.4;
  font-family: ${theme.typography.fontFamily};
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes.base};
  }
`;

type WizardStep = 'platform' | 'league-input' | 'team-select' | 'matchup-select' | 'host-select' | 'lead-capture' | 'call' | 'espn-private-league';

interface LeagueData {
  league: League;
  rosters: Roster[];
  matchups: Matchup[];
  users: User[];
}

interface FormData {
  name: string;
  email: string;
}

function App() {
  // Check if we're on the test page
  const isTestPage = window.location.pathname.includes('test') || window.location.search.includes('test');
  
  // Skip intro - go directly to platform selection
  
  // Check if a platform was selected before (for OAuth redirects)
  const [selectedPlatform, setSelectedPlatform] = useState<'sleeper' | 'espn' | 'yahoo' | 'direct' | null>(() => {
    const savedPlatform = localStorage.getItem('selectedPlatform');
    return savedPlatform as 'sleeper' | 'espn' | 'yahoo' | 'direct' | null;
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leagueData, setLeagueData] = useState<LeagueData | null>(null);
  const [leagueInput, setLeagueInput] = useState<LeagueInputType | null>(null);
  
  // New wizard flow states - start directly on platform selection
  const [wizardStep, setWizardStep] = useState<WizardStep>('platform');
  const [selectedTeam, setSelectedTeam] = useState<Roster | null>(null);
  const [selectedMatchups, setSelectedMatchups] = useState<Matchup[]>([]);
  const [selectedHost, setSelectedHost] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<FormData | null>(null);
  const [podcastPrompt, setPodcastPrompt] = useState<PodcastPromptData | null>(null);
  const [isRecordingView, setIsRecordingView] = useState(false);
  const [recordingCallId, setRecordingCallId] = useState<string | null>(null);

  // Check for OAuth callback parameters and load player data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthParams = urlParams.get('success') || urlParams.get('access_token') || urlParams.get('error');
    
    // If we have OAuth params, ensure we're on the Yahoo platform
    if (hasOAuthParams && !selectedPlatform) {
      setSelectedPlatform('yahoo');
      localStorage.setItem('selectedPlatform', 'yahoo');
    }
    
    // Check for recording hash in URL
    const checkRecordingHash = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#recording-')) {
        const callId = hash.replace('#recording-', '');
        setIsRecordingView(true);
        setRecordingCallId(callId);
      } else if (isRecordingView) {
        // If we're leaving a recording view, clear it
        setIsRecordingView(false);
        setRecordingCallId(null);
      }
    };
    
    // Check initial hash
    checkRecordingHash();
    
    // Listen for hash changes
    const handleHashChange = () => {
      checkRecordingHash();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    // Preload player data
    playerDataService.loadPlayerData().catch(err => {
      // Silently handle player data load error
    });
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isRecordingView]);

  const handleLeagueSubmit = async (input: LeagueInputType) => {
    setLoading(true);
    setError(null);
    setLeagueInput(input);

    // Track league load attempt
    analytics.trackLeagueLoad(input.platform, false, input.leagueId);

    try {
      // Ensure player data is loaded first
      console.log('Loading player data...');
      await playerDataService.loadPlayerData();
      
      const data = await fantasyApi.getLeagueData(input, 1); // Default to week 1
      
      console.log('League data response:', {
        league: data.league.success,
        rosters: data.rosters.success,
        users: data.users.success,
        matchups: data.matchups.success,
        rosterCount: data.rosters.data?.length,
        firstRoster: data.rosters.data?.[0]
      });
      
      // Check if any of the main data failed
      if (!data.league.success) {
        throw new Error(data.league.error || 'Failed to load league data');
      }

      // Extract successful data
      const league = data.league.data!;
      const rosters = data.rosters.success ? data.rosters.data! : [];
      const matchups = data.matchups.success ? data.matchups.data! : [];
      const users = data.users.success ? data.users.data! : [];

      console.log('Final league data structure:', {
        league: league,
        rosterCount: rosters.length,
        matchupCount: matchups.length,
        userCount: users.length,
        firstRosterPlayers: rosters[0]?.players?.length || 0
      });

      // Validate roster data
      if (rosters.length === 0) {
        throw new Error('No roster data found. Please check your league ID and try again.');
      }

      // Check if rosters have players
      const rostersWithPlayers = rosters.filter(r => r.players && r.players.length > 0);
      if (rostersWithPlayers.length === 0) {
        throw new Error('Rosters found but no player data available. This may be a league configuration issue.');
      }

      setLeagueData({
        league,
        rosters,
        matchups,
        users
      });

      // Track successful league load
      analytics.trackLeagueLoad(input.platform, true, input.leagueId);

      // Move to team selection step after successfully loading league data
      setWizardStep('team-select');
      trackWizardStep('team-select', { 
        platform: input.platform, 
        league_id: input.leagueId,
        team_count: rosters.length 
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      
      // Check if this is an ESPN private league error
      if (input.platform === 'espn' && errorMessage.toLowerCase().includes('private league detected')) {
        // Go to ESPN private league page instead of showing error
        setWizardStep('espn-private-league');
        trackWizardStep('espn-private-league', { 
          platform: input.platform, 
          league_id: input.leagueId,
          error_type: 'private_league'
        });
      } else {
        // Show regular error for other cases
        setError(errorMessage);
      }
      
      // Track error
      analytics.trackLeagueLoad(input.platform, false, input.leagueId, errorMessage);
      trackError('league_load_failed', errorMessage, { platform: input.platform });
    } finally {
      setLoading(false);
    }
  };


  const handleReset = () => {
    setLeagueData(null);
    setError(null);
    setLeagueInput(null);
    setSelectedPlatform(null);
    setSelectedTeam(null);
    setSelectedMatchups([]);
    setSelectedHost(null);
    setUserInfo(null);
    setWizardStep('platform');
    localStorage.removeItem('selectedPlatform');
    // Don't reset intro - user doesn't need to see it again
  };

  const handleRetry = () => {
    if (leagueInput) {
      handleLeagueSubmit(leagueInput);
    }
  };

  // Update wizard step based on state
  useEffect(() => {
    if (!selectedPlatform) {
      setWizardStep('platform');
      trackWizardStep('platform');
    } else if (selectedPlatform === 'direct') {
      // Direct platform goes straight to lead capture, don't override
      return;
    } else if (!leagueData && wizardStep !== 'espn-private-league') {
      // Only set league-input if we're not on the private league page
      setWizardStep('league-input');
      trackWizardStep('league-input', { platform: selectedPlatform });
    }
  }, [selectedPlatform, leagueData, wizardStep]);

  // Handle team selection
  const handleTeamSelect = (roster: Roster) => {
    setSelectedTeam(roster);
    
    // Track team selection
    analytics.trackTeamSelection(roster.teamName || 'Unknown Team', roster.players?.length || 0);
    
    // Skip matchup selection and go directly to host selection
    // All matchups will be automatically included
    setWizardStep('host-select');
    trackWizardStep('host-select', { 
      team_name: roster.teamName,
      roster_size: roster.players?.length 
    });
  };

  // Handle matchup selection
  const handleMatchupSelect = (matchups: Matchup[]) => {
    setSelectedMatchups(matchups);
    setWizardStep('host-select');
  };

  // Handle host selection
  const handleHostSelect = (hostId: string) => {
    setSelectedHost(hostId);
    
    // Track host selection
    analytics.trackHostSelection(hostId);
    
    // For direct platform with user info, go straight to call
    if (selectedPlatform === 'direct' && userInfo) {
      setWizardStep('call');
      trackWizardStep('call', { 
        platform: 'direct',
        host: hostId,
        flow_type: 'direct_talk'
      });
      analytics.trackConversion('call_initiated');
    } else {
      setWizardStep('lead-capture');
      trackWizardStep('lead-capture', { selected_host: hostId });
    }
  };

  // Handle lead capture
  const handleLeadCapture = async (data: FormData) => {
    setUserInfo(data);
    
    // Track lead form submission
    analytics.trackLeadFormSubmit(data);
    analytics.trackConversion('lead_captured');
    
    // Handle direct platform case (no league data)
    if (selectedPlatform === 'direct') {
      // For direct talk, go to host selection
      setWizardStep('host-select');
      trackWizardStep('host-select', { 
        platform: 'direct',
        flow_type: 'direct_talk' 
      });
      return;
    }
    
    // Generate podcast prompt with all matchups for league-based flow
    if (selectedTeam && leagueData && selectedHost) {
      try {
        const promptData = generatePodcastPrompt(
          selectedTeam,
          leagueData.rosters,
          leagueData.matchups, // Use all matchups from league data
          selectedHost,
          data
        );
        
        setPodcastPrompt(promptData);
        console.log('Generated Podcast Prompt:', promptData.prompt);
        console.log('Team Info for Agent:', promptData.team_info);
        
        setWizardStep('call');
        trackWizardStep('call', { 
          platform: leagueInput?.platform,
          host: selectedHost,
          team: selectedTeam.teamName 
        });
        analytics.trackConversion('call_initiated');
      } catch (err) {
        const errorMsg = 'Failed to generate podcast prompt. Please try again.';
        setError(errorMsg);
        trackError('prompt_generation_failed', errorMsg, { 
          platform: leagueInput?.platform 
        });
      }
    }
  };

  // Show test page if URL contains 'test'
  if (isTestPage) {
    return <TestCallPage />;
  }
  
  // Show recording view if accessing a shared recording
  if (isRecordingView && recordingCallId) {
    return (
      <AppContainer>
        <MainContent>
          <SharedRecordingView
            callId={recordingCallId}
            onNavigateHome={() => {
              // Just go back to main menu, keep user info
              setIsRecordingView(false);
              setRecordingCallId(null);
              setSelectedPlatform(null);
              setWizardStep('platform');
              setLeagueData(null);
              setLeagueInput(null);
              setSelectedTeam(null);
              setSelectedMatchups([]);
              setSelectedHost(null);
              setPodcastPrompt(null);
              setError(null);
              setLoading(false);
              
              // Clear URL hash
              window.location.hash = '';
              
              // Track analytics
              trackWizardStep('platform', { flow_type: 'new_podcast_from_recording' });
            }}
          />
        </MainContent>
        <PoweredByYobi />
      </AppContainer>
    );
  }
  
  return (
    <AppContainer>
      <MainContent>
        {/* Loading State */}
        {loading && (
          <LoadingSpinner 
            message="Loading league data..." 
            subMessage="This may take a few seconds"
          />
        )}

        {/* Error State */}
        {error && !loading && (
          <ErrorMessage 
            error={error} 
            platform={leagueInput?.platform}
            onRetry={handleRetry}
            onBack={handleReset}
            onLogout={() => {
              setLeagueData(null);
              setError(null);
              setLeagueInput(null);
              localStorage.removeItem('yahoo_access_token');
              localStorage.removeItem('yahoo_leagues');
            }}
          />
        )}

        {/* Wizard Steps */}
        {!loading && !error && (
          <>
            {wizardStep === 'platform' && (
              <PlatformWizard onSelectPlatform={(platform) => {
                setSelectedPlatform(platform);
                localStorage.setItem('selectedPlatform', platform);
                if (platform === 'direct') {
                  // Skip league input for direct talk option, go straight to lead form
                  setWizardStep('lead-capture');
                } else {
                  setWizardStep('league-input');
                }
              }} />
            )}

            {wizardStep === 'league-input' && selectedPlatform && selectedPlatform !== 'direct' && (
              <>
                {selectedPlatform === 'yahoo' ? (
                  <YahooLogin 
                    onLeagueSelect={handleLeagueSubmit}
                    onBack={() => {
                      setSelectedPlatform(null);
                      localStorage.removeItem('selectedPlatform');
                      setWizardStep('platform');
                    }}
                  />
                ) : (
                  <SimpleLeagueInput 
                    onSubmit={handleLeagueSubmit} 
                    loading={loading} 
                    platform={selectedPlatform}
                    onBack={() => {
                      setSelectedPlatform(null);
                      localStorage.removeItem('selectedPlatform');
                      setWizardStep('platform');
                    }}
                  />
                )}
              </>
            )}

            {wizardStep === 'team-select' && leagueData && (
              <TeamSelector 
                rosters={leagueData.rosters}
                onSelect={handleTeamSelect}
              />
            )}

            {/* Matchup selection is now automatic - all matchups are included
            {wizardStep === 'matchup-select' && leagueData && (
              <MatchupSelector 
                matchups={leagueData.matchups}
                onSelect={handleMatchupSelect}
              />
            )} */}

            {wizardStep === 'host-select' && (
              <HostSelector 
                onSelect={handleHostSelect}
              />
            )}

            {wizardStep === 'lead-capture' && (
              <LeadCaptureForm 
                onSubmit={handleLeadCapture}
                onBack={() => {
                  // If we have league data, go back to host selection
                  // Otherwise, go back to platform selection
                  if (leagueData) {
                    setWizardStep('host-select');
                  } else {
                    setSelectedPlatform(null);
                    localStorage.removeItem('selectedPlatform');
                    setWizardStep('platform');
                  }
                }}
              />
            )}

            {wizardStep === 'espn-private-league' && (
              <ESPNPrivateLeague 
                onNext={() => {
                  // Go directly to lead capture form for private league users
                  setWizardStep('lead-capture');
                  trackWizardStep('lead-capture', { 
                    source: 'espn-private-league',
                    platform: selectedPlatform || 'espn'
                  });
                }}
              />
            )}

            {wizardStep === 'call' && userInfo && (
              <CallView 
                userData={{
                  name: userInfo.name,
                  email: userInfo.email
                }}
                podcastPrompt={podcastPrompt}
                selectedHost={selectedHost || 'Tony'}
                onCallEnd={() => {
                  // User clicked "No" on restart prompt
                  // Could navigate to a thank you page or just stay on the ended state
                }}
              />
            )}
          </>
        )}
      </MainContent>
      
      {wizardStep !== 'host-select' && <PoweredByYobi />}
    </AppContainer>
  );
}

export default App;